import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Skeleton,
} from "@mui/material";
import {
  CheckCircle,
  Schedule,
  School,
  Cancel,
  PersonOff,
  HowToReg,
  Event,
  EmojiEvents,
} from "@mui/icons-material";
import { BASE_URL } from "../../../services/configUrls";
import LimitsSection from "../../../components/Event/LimitsSection";
import AdvanceBook from "./AdvanceBook";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";

const StatusCards = () => {
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [limitsData, setLimitsData] = useState(null);
  const [limitsLoading, setLimitsLoading] = useState(true);
  const [bookTechCampOpen, setBookTechCampOpen] = useState(false);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/event/analytics/institute-summary`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );
        const data = await response.json();
        setSummaryData(data.summary);
      } catch (error) {
        console.error("Error fetching summary data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  // useEffect(() => {
  //   const fetchLimits = async () => {
  //     try {
  //       const response = await fetch(
  //         `${BASE_URL}/event/fdp_tech_camp_limits`,
  //         {
  //           method: "GET",
  //           headers: {
  //             "Content-Type": "application/json",
  //             Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
  //           },
  //         }
  //       );
  //       const data = await response.json();
  //       setLimitsData(data);
  //     } catch (error) {
  //       console.error("Error fetching limits data:", error);
  //     } finally {
  //       setLimitsLoading(false);
  //     }
  //   };

  //   fetchLimits();
  // }, []);

  // Mapping of API keys to display configuration
  const statusMapping = {
    hosted: {
      label: "Hosted",
      icon: <Event sx={{ fontSize: 20 }} />,
      color: "#00bcd4",
      bgColor: "#e0f7fa",
    },
    participated: {
      label: "Participated",
      icon: <CheckCircle sx={{ fontSize: 20 }} />,
      color: "#4caf50",
      bgColor: "#e8f5e9",
    },
    open_nomination: {
      label: "Open Nomination",
      icon: <HowToReg sx={{ fontSize: 20 }} />,
      color: "#9c27b0",
      bgColor: "#f3e5f5",
    },
    trained: {
      label: "Nominated",
      icon: <School sx={{ fontSize: 20 }} />,
      color: "#2196f3",
      bgColor: "#e3f2fd",
    },
    passed: {
      label: "Passed",
      icon: <EmojiEvents sx={{ fontSize: 20 }} />,
      color: "#4caf50",
      bgColor: "#e8f5e9",
    },
    failed: {
      label: "Failed",
      icon: <Cancel sx={{ fontSize: 20 }} />,
      color: "#f44336",
      bgColor: "#ffebee",
    },
    dropout: {
      label: "Dropout",
      icon: <PersonOff sx={{ fontSize: 20 }} />,
      color: "#ff9800",
      bgColor: "#fff3e0",
    },
  };

  const getStatusDataForFDP = () => {
    if (!summaryData || !summaryData.fdp) {
      return { firstRow: [], secondRow: [] };
    }

    const data = summaryData.fdp;

    const firstRow = [
      {
        label: "Hosted",
        count: data.hosted || 0,
        icon: statusMapping.hosted.icon,
        color: statusMapping.hosted.color,
        bgColor: statusMapping.hosted.bgColor,
      },
      {
        label: "Participated",
        count: data.participated || 0,
        icon: statusMapping.participated.icon,
        color: statusMapping.participated.color,
        bgColor: statusMapping.participated.bgColor,
      },
      {
        label: "Open Nomination",
        count: data.open_nomination || 0,
        icon: statusMapping.open_nomination.icon,
        color: statusMapping.open_nomination.color,
        bgColor: statusMapping.open_nomination.bgColor,
      },
    ];

    const secondRow = [
      {
        label: "Nominated",
        count: data.nominated || 0,
        icon: statusMapping.trained.icon,
        color: statusMapping.trained.color,
        bgColor: statusMapping.trained.bgColor,
      },
      {
        label: "Passed",
        count: data.trained || 0, // Assuming passed is same as trained
        icon: statusMapping.passed.icon,
        color: statusMapping.passed.color,
        bgColor: statusMapping.passed.bgColor,
      },
      {
        label: "Failed",
        count: data.failed || 0,
        icon: statusMapping.failed.icon,
        color: statusMapping.failed.color,
        bgColor: statusMapping.failed.bgColor,
      },
    ];

    return { firstRow, secondRow };
  };

  const getStatusDataForEDP = () => {
    if (!summaryData || !summaryData.edp) {
      return { firstRow: [], secondRow: [] };
    }

    const data = summaryData.edp;

    const firstRow = [
      {
        label: "Participated",
        count: data.participated || 0,
        icon: statusMapping.participated.icon,
        color: statusMapping.participated.color,
        bgColor: statusMapping.participated.bgColor,
      },
      {
        label: "Open Nomination",
        count: data.open_nomination || 0,
        icon: statusMapping.open_nomination.icon,
        color: statusMapping.open_nomination.color,
        bgColor: statusMapping.open_nomination.bgColor,
      },
    ];

    const secondRow = [
      {
        label: "Nominated",
        count: data.nominated || 0,
        icon: statusMapping.trained.icon,
        color: statusMapping.trained.color,
        bgColor: statusMapping.trained.bgColor,
      },
      {
        label: "Passed",
        count: data.trained || 0, // Assuming passed is same as trained
        icon: statusMapping.passed.icon,
        color: statusMapping.passed.color,
        bgColor: statusMapping.passed.bgColor,
      },
      {
        label: "Failed",
        count: data.failed || 0,
        icon: statusMapping.failed.icon,
        color: statusMapping.failed.color,
        bgColor: statusMapping.failed.bgColor,
      },
    ];

    return { firstRow, secondRow };
  };

  const getStatusDataForTechCamp = () => {
    if (!summaryData || !summaryData.tech_camp) {
      return { hostedRow: [], studentRow: [] };
    }

    const data = summaryData.tech_camp;

    const hostedRow = [
      {
        label: "Hosted",
        count: data.hosted || 0,
        icon: statusMapping.hosted.icon,
        color: statusMapping.hosted.color,
        bgColor: statusMapping.hosted.bgColor,
      },
    ];

    const studentRow = [
      {
        label: "Nominated",
        count: data.nominated || 0,
        icon: statusMapping.trained.icon,
        color: statusMapping.trained.color,
        bgColor: statusMapping.trained.bgColor,
      },
      {
        label: "Passed",
        count: data.trained || 0, // Assuming passed is same as trained
        icon: statusMapping.passed.icon,
        color: statusMapping.passed.color,
        bgColor: statusMapping.passed.bgColor,
      },
      {
        label: "Dropout",
        count: data.dropout || 0,
        icon: statusMapping.dropout.icon,
        color: statusMapping.dropout.color,
        bgColor: statusMapping.dropout.bgColor,
      },
    ];

    return { hostedRow, studentRow };
  };

  const StatusCard = ({ title, firstRow, secondRow, sectionLabels }) => (
    <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "#1a1a1a", fontSize: "1rem" }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              ml: 0.75,
              width: 18,
              height: 18,
              borderRadius: "50%",
              border: "2px solid #5e6ad2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#5e6ad2",
              fontSize: "11px",
              fontWeight: 600,
            }}
          >
            i
          </Box>
        </Box>

        {/* First Row */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: `repeat(${firstRow.length}, 1fr)`,
              },
              gap: 1.5,
            }}
          >
            {firstRow.map((item, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 1.5,
                  backgroundColor: item.bgColor,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: item.color,
                    borderRadius: 1,
                    p: 0.75,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    minWidth: 36,
                    minHeight: 36,
                  }}
                >
                  {item.icon}
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#1a1a1a",
                      fontSize: "1.25rem",
                      lineHeight: 1.2,
                    }}
                  >
                    {item.count}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6b7280",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Section Label */}
        {sectionLabels && (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: "#1a1a1a",
              mb: 1,
              fontSize: "0.875rem",
            }}
          >
            {sectionLabels}
          </Typography>
        )}

        {/* Second Row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: `repeat(${secondRow.length}, 1fr)`,
            },
            gap: 1.5,
          }}
        >
          {secondRow.map((item, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: item.bgColor,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  backgroundColor: item.color,
                  borderRadius: 1,
                  p: 0.75,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  minWidth: 36,
                  minHeight: 36,
                }}
              >
                {item.icon}
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#1a1a1a",
                    fontSize: "1.25rem",
                    lineHeight: 1.2,
                  }}
                >
                  {item.count}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#6b7280",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const TechCampStatusCard = ({ title, hostedRow, studentRow }) => (
    <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "#1a1a1a", fontSize: "1rem" }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              ml: 0.75,
              width: 18,
              height: 18,
              borderRadius: "50%",
              border: "2px solid #5e6ad2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#5e6ad2",
              fontSize: "11px",
              fontWeight: 600,
            }}
          >
            i
          </Box>
        </Box>

        {/* Hosted Row */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: `repeat(${hostedRow.length}, 1fr)`,
              },
              gap: 1.5,
            }}
          >
            {hostedRow.map((item, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 1.5,
                  backgroundColor: item.bgColor,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: item.color,
                    borderRadius: 1,
                    p: 0.75,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    minWidth: 36,
                    minHeight: 36,
                  }}
                >
                  {item.icon}
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#1a1a1a",
                      fontSize: "1.25rem",
                      lineHeight: 1.2,
                    }}
                  >
                    {item.count}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6b7280",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Student Label */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "#1a1a1a",
            mb: 1,
            fontSize: "0.875rem",
          }}
        >
          Student
        </Typography>

        {/* Student Row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: `repeat(${studentRow.length}, 1fr)`,
            },
            gap: 1.5,
          }}
        >
          {studentRow.map((item, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: item.bgColor,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  backgroundColor: item.color,
                  borderRadius: 1,
                  p: 0.75,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  minWidth: 36,
                  minHeight: 36,
                }}
              >
                {item.icon}
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#1a1a1a",
                    fontSize: "1.25rem",
                    lineHeight: 1.2,
                  }}
                >
                  {item.count}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#6b7280",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const SkeletonCard = () => (
    <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Skeleton variant="text" width={120} height={24} />
        </Box>

        {/* First Row Skeleton */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1.5,
            }}
          >
            {[1, 2, 3].map((item) => (
              <Paper
                key={item}
                elevation={0}
                sx={{
                  p: 1.5,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width={36}
                  height={36}
                  sx={{ borderRadius: 1 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width={40} height={28} />
                  <Skeleton variant="text" width={70} height={20} />
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Section Label Skeleton */}
        <Skeleton variant="text" width={60} height={20} sx={{ mb: 1 }} />

        {/* Second Row Skeleton */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 1.5,
          }}
        >
          {[1, 2, 3].map((item) => (
            <Paper
              key={item}
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: "#f5f5f5",
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Skeleton
                variant="rectangular"
                width={36}
                height={36}
                sx={{ borderRadius: 1 }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={40} height={28} />
                <Skeleton variant="text" width={70} height={20} />
              </Box>
            </Paper>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
          <SkeletonCard />
          <SkeletonCard />
        </Box>
      </Box>
    );
  }

  const fdpData = getStatusDataForFDP();
  // const edpData = getStatusDataForEDP();
  const techCampData = getStatusDataForTechCamp();

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Limits Section */}
      {/* <LimitsSection
        limitsData={limitsData}
        loading={limitsLoading}
        onBookNow={() => setBookTechCampOpen(true)}
      /> */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <StatusCard
          title="FDP Status"
          firstRow={fdpData.firstRow}
          secondRow={fdpData.secondRow}
          sectionLabels="Faculty"
        />
        {/* <StatusCard
          title="EDP Status"
          firstRow={edpData.firstRow}
          secondRow={edpData.secondRow}
          sectionLabels="Faculty"
        /> */}
        <TechCampStatusCard
          title="Tech Camp Status"
          hostedRow={techCampData.hostedRow}
          studentRow={techCampData.studentRow}
        />
      </Box>
      {/* Tech Camp booking dialog — opened from Book Now in LimitsSection */}
      <Dialog
        open={bookTechCampOpen}
        onClose={() => setBookTechCampOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", position: "relative", p: 0 } }}
      >
        <IconButton
          onClick={() => setBookTechCampOpen(false)}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            zIndex: 1000,
            bgcolor: "rgba(255,255,255,0.85)",
            "&:hover": { bgcolor: "#fff" },
          }}
        >
          <Close />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          <AdvanceBook
            forcedEventType="tech_camp"
            onClose={(isSuccess) => {
              setBookTechCampOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default StatusCards;
